import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { GetStartedService } from "../components/get-started/types";

export interface CartItem {
  id: number | null;
  test_title: string;
  service_title: string;
  quantity: number;
  price: number;
  net_price?: number;
  discount?: number;
}

const getDefaultCartItem = (): CartItem => ({
  id: null,
  test_title: "",
  service_title: "",
  quantity: 0,
  price: 0,
  net_price: 0,
  discount: 0,
});

export const useCartStore = defineStore(
  "cart",
  () => {
    // State
    const cartItems = ref<CartItem[]>([]);
    const cartItemToAdd = ref<CartItem>(getDefaultCartItem());
    const isCartDrawerOpen = ref<boolean>(false);
    const isHomeTestEnabled = ref<boolean>(false);

    // Getters (computed properties)
    const totalItems = computed<number>(() => {
      return cartItems.value.reduce((accumulatedItems, item) => {
        return accumulatedItems + item.quantity;
      }, 0);
    });

    const totalPrice = computed<number>(() => {
      return cartItems.value.reduce((accumulatedPrice, item) => {
        const itemPrice = item.net_price && item.discount && item.discount > 0 ? item.net_price : item.price;
        return accumulatedPrice + itemPrice * item.quantity;
      }, 0);
    });

    // Actions (functions)
    function toggleCartDrawer() {
      isCartDrawerOpen.value = !isCartDrawerOpen.value;
    }

    function openCartDrawer() {
      isCartDrawerOpen.value = true;
    }

    function closeCartDrawer() {
      isCartDrawerOpen.value = false;
    }

    function pushToCart() {
      const item = cartItemToAdd.value;

      const isItemIncomplete =
        item.id == null ||
        !item.test_title ||
        !item.service_title ||
        typeof item.price !== "number" ||
        typeof item.quantity !== "number";

      if (isItemIncomplete) {
        console.error("Cannot commit incomplete item to cart:", item);
        return;
      }

      const existingItem = cartItems.value.find((cartItem) => cartItem.id === item.id);

      if (existingItem) {
        existingItem.quantity = item.quantity;
      } else {
        cartItems.value.push(item);
      }

      cartItemToAdd.value = getDefaultCartItem();
    }

    function removeItem(itemId: number) {
      const index = cartItems.value.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        cartItems.value.splice(index, 1);
      }
    }

    function clearCart() {
      cartItems.value = [];
    }

    function updateItemQuantity(itemId: number, quantity: number) {
      if (quantity <= 0) {
        removeItem(itemId);
        return;
      }

      const item = cartItems.value.find((cartItem) => cartItem.id === itemId);
      if (item) {
        item.quantity = quantity;
      }
    }

    const resetHomeTestState = async () => {
      try {
        const servicesResult = await useServiceListType();
        const services = servicesResult.data.value?.data ?? [];

        const homeTestEnabled = services.some((service: GetStartedService) => service.slug === "home-test-kit");
        isHomeTestEnabled.value = homeTestEnabled || false;
      } catch (err) {
        console.error("[cart] resetHomeTestState failed:", err);
        isHomeTestEnabled.value = false;
      }
    };

    return {
      cartItems,
      cartItemToAdd,
      isCartDrawerOpen,
      totalItems,
      totalPrice,
      toggleCartDrawer,
      openCartDrawer,
      closeCartDrawer,
      pushToCart,
      removeItem,
      clearCart,
      updateItemQuantity,
      resetHomeTestState,
      isHomeTestEnabled,
    };
  },
  {
    persist: {
      storage: persistedState.cookiesWithOptions({
        sameSite: "lax",
        maxAge: 60 * 60 * 24 * 7,
      }),
      paths: ["cartItems"],
      key: "cartSession",
    },
  }
);
